import {resolveInstitutionDisplayName} from '../../lib/institutionDisplayNames.ts';
import {getCreditCardDisplayNameByPrefix} from '../../components/specific/CreditCard/util/detectSchemeFromCreditCardNumber.ts';

export enum BankAccountType {
    OPERATIONAL = 'OPERATIONAL',
    SAVINGS = 'SAVINGS',
    CREDIT_CARD = 'CREDIT_CARD',
}

export interface BankAccountData {
    account_id: string;
    iban: string | null;
    currency: string ;
    owner_name: string;
    business_id?: string;
    institution_name: string;
    balance: number;
    connected_on?: string | null;
    refreshed_on?: string | null;
    last_transaction_full_sync_on?: string | null;
    is_expired?: boolean;
    expires_soon?: boolean;
    institution_logo_url?: string | null;
    account_type?: BankAccountType;
    credit_card_number_prefix?: string | null;
    credit_card_number_suffix?: string | null;
}

export class CustomerBankAccount {
    public id: string;
    public iban: string | null;
    public currency: string;
    public ownerName: string;
    public businessId?: string;
    public institutionName: string;
    public balance: number;
    public connectedOn: string;
    public refreshedOn: string;
    public lastTransactionFullSyncOn: string;

    constructor(
        id: string,
        iban: string | null,
        currency: string,
        ownerName: string,
        businessId: string = "",
        institutionName: string,
        balance: number,
        connectedOn: string,
        refreshedOn: string,
        lastTransactionFullSyncOn: string,
        public readonly isExpired: boolean,
        public readonly expiresSoon: boolean,
        public logoUrl: string = "",
        public readonly accountType: BankAccountType = BankAccountType.OPERATIONAL,
        public readonly creditCardNumberPrefix: string | null = null,
        public readonly creditCardNumberSuffix: string | null = null,
    ) {
        this.id = id;
        this.iban = iban;
        this.currency = currency;
        this.ownerName = ownerName;
        this.businessId = businessId;
        this.institutionName = institutionName;
        this.balance = balance;
        this.connectedOn = connectedOn;
        this.refreshedOn = refreshedOn;
        this.lastTransactionFullSyncOn = lastTransactionFullSyncOn;
    }

    public displayIdentifier(): string {
        if (this.accountType === BankAccountType.CREDIT_CARD) {
            if (!this.creditCardNumberPrefix && !this.creditCardNumberSuffix) {
                return '';
            }
            return `${this.creditCardNumberPrefix ?? ''} •••• ${this.creditCardNumberSuffix ?? ''}`;
        }
        if (this.iban) {
            return this.iban;
        }
        return resolveInstitutionDisplayName(this.institutionName);
    }

    public hasIban(): this is CustomerBankAccountWithIban {
        return this.iban != null && this.iban.length > 0;
    }

    public isCreditCard(): boolean {
        return this.accountType === BankAccountType.CREDIT_CARD;
    }

    public isOperational(): boolean {
        return this.accountType === BankAccountType.OPERATIONAL;
    }

    public canReceivePayments(): this is CustomerBankAccountWithIban {
        return this.isOperational() && this.hasIban();
    }

    public displayInstitutionName(): string {
        if (this.accountType === BankAccountType.CREDIT_CARD && !this.institutionName?.trim()) {
            return getCreditCardDisplayNameByPrefix(this.creditCardNumberPrefix);
        }
        if (this.accountType === BankAccountType.SAVINGS && !this.institutionName?.trim()) {
            return 'Savings account';
        }
        return resolveInstitutionDisplayName(this.institutionName);
    }

    public withLogoUrl(logoUrl: string): CustomerBankAccount {
        return new CustomerBankAccount(
            this.id,
            this.iban,
            this.currency,
            this.ownerName,
            this.businessId,
            this.institutionName,
            this.balance,
            this.connectedOn,
            this.refreshedOn,
            this.lastTransactionFullSyncOn,
            this.isExpired,
            this.expiresSoon,
            logoUrl,
            this.accountType,
            this.creditCardNumberPrefix,
            this.creditCardNumberSuffix,
        )
    }

    public static fromApiResponse(data: BankAccountData): CustomerBankAccount {
        return new CustomerBankAccount(
            data.account_id,
            data.iban ?? '',
            data.currency,
            data.owner_name,
            data.business_id,
            data.institution_name,
            data.balance,
            data.connected_on ?? '',
            data.refreshed_on ?? '',
            data.last_transaction_full_sync_on,
            data.is_expired,
            data.expires_soon,
            data.institution_logo_url ?? '',
            data.account_type,
            data.credit_card_number_prefix ?? null,
            data.credit_card_number_suffix ?? null,
        );
    }
}

export type CustomerBankAccountWithIban = CustomerBankAccount & {iban: string}

export function filterBankAccountsWithIban(
    accounts: CustomerBankAccount[],
): CustomerBankAccountWithIban[] {
    return accounts.filter((account): account is CustomerBankAccountWithIban => account.hasIban());
}

export function filterBankAccountsThatCanReceivePayments(
    accounts: CustomerBankAccount[],
): CustomerBankAccountWithIban[] {
    return accounts.filter((account): account is CustomerBankAccountWithIban => account.canReceivePayments());
}
